import { NonAppNavWrapper } from '@/components/generic/non-app-nav'
import ToolLinks from '@/components/page-specific/tools/tool-links'
import { useAuth, useUser } from '@clerk/nextjs'
import { ArrowRight, Check } from 'lucide-react'
import { NextSeo } from 'next-seo'
import { Inter } from 'next/font/google'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'

const inter = Inter({ subsets: ['latin'] })

export default function ReportGenerator() {
  const router = useRouter()
  const { isSignedIn } = useAuth()

  const { user } = useUser()
  const isSubscribed = user?.publicMetadata.isSubscribed

  return (
    <>
      <NextSeo
        title="v0 report - The Revolutionary AI Report Generator"
        description="Discover the power of AI with the v0 AI Report Generator – your ultimate report-writing tool for detailed, accurate, and insightful reports effortlessly."
      />
      <NonAppNavWrapper>
        <main className={`scroll-smooth flex min-h-screen flex-col ${inter.className}`}>
          <div className=" mt-36">
            <div className="mx-auto text-center">
              <h1 className="text-6xl text-zinc-700  grid gap-2">
                <span className="font-medium">v0 Report - The Revolutionary </span>
                <span className="font-thin italic font-serif leading-none text-yellow-600">
                  AI Report Generator
                </span>
              </h1>
            </div>
            <div className="ml-auto flex px-4 md:px-0 md:w-3/4 mx-auto mb-24">
              <div className="w-full mx-auto">
                <div className="grid gap-6">
                  <h2 className="text-4xl text-zinc-700 text-left mt-48">
                    <span className="font-medium">A Smarter Approach to </span>
                    <span className="font-thin italic font-serif leading-none text-sky-600">
                      Creating Reports
                    </span>
                  </h2>
                  <p className="text-lg text-zinc-600 grid gap-2">
                    <span>
                      The v0 AI Report Generator is designed to streamline your data report analysis
                      and reporting processes.{' '}
                    </span>
                    <span>
                      Instantly turn your data into visually impressive, expert-quality reports. No
                      more hours spent creating impactful informational reports—let our AI report
                      maker handle your report work for you.
                    </span>
                  </p>
                  <Link
                    className="w-fit h-10 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                    href={'/auth/signup'}
                  >
                    {' '}
                    Get started <ArrowRight className="ml-2 w-4 h-4" />
                  </Link>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/report.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Why Choose v0’s AI Report Generator Tool?
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Quick Market Analysis Report</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Get your report in just 10 seconds. v0 Report quickly generates a
                            complete report. Just tell us what you need, whether it’s to visualize
                            survey results, compile a performance review, outline a marketing
                            summary, or analyze any reports in seconds.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Easy Report Design</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Create instantly with your text. If you have a lot of text and aren't
                            sure where to begin, just paste your text into v0 Report. It'll quickly
                            create a professional-looking report that saves you time.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">AI-Powered Report Generation</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Find the perfect report template for any report. Whether it’s for
                            surveys, performance reviews, marketing summaries, or analyses, our AI
                            report generator offers a variety of customizable templates to suit your
                            performance report needs.
                          </p>{' '}
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Make It Your Own</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Create a report just for you. With v0 Report's easy-to-use editor, you
                            can personalize your comprehensive reports. Choose from our wide
                            selection of design elements, fonts, and colors to match your brand
                            quickly and easily.
                          </p>{' '}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <Image
                    className="rounded-2xl w-full md:w-3/4"
                    src="/images/tools/on-laptop.webp"
                    alt="Image"
                    width={500}
                    height={300}
                  />

                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    Relied by Top Professionals
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Marketers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            <ul>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Turn marketing campaign results, customer demographics, and market
                                trends into visual, data-driven insights.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Produce clear, engaging and real fast reports for clients that
                                highlight campaign performance, and essential marketing strategies.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Spot and predict market trends to stay ahead.
                              </li>
                            </ul>
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">HR and Learning & Development</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            <ul>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Showcase progress, areas for improvement, and support effective
                                performance reviews.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Visually demonstrate the success and impact of training programs.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Gather and analyze data on workforce diversity, employee
                                satisfaction, and company culture.
                              </li>
                            </ul>
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Government Organizations</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            <ul>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Depict community impact, project progress, beneficiary stories, and
                                fund utilization for donor reports and stakeholder briefings.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Create reports on donation impact, program success, and financial
                                health of the organization.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Evaluate and improve programs by analyzing data to guide future
                                project planning and resource management.
                              </li>
                            </ul>
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Researchers</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            <ul>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Convert complex data sets into clear, easy-to-understand visuals to
                                enhance research presentations.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Simplify report sharing and editing, crucial for collaborative
                                studies and publications.
                              </li>
                              <li className="flex items-center">
                                <span className="inline-block w-1 h-1 bg-black rounded-full mr-2" />
                                Prepare reports for academic journals or conferences, meeting
                                publication standards.
                              </li>
                            </ul>
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    How to Create AI Reports Easily
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Describe Your Report</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Write a short description (up to 120 characters) of your report's
                            purpose and content creation, whether for a survey, performance review,
                            marketing summary, or report analysis.
                          </p>
                        </div>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Upload your data</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            Upload your data, or v0 can scrape the web, and v0 Report will analyze
                            your data and generate a report based on the information provided.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="grid gap-6 mt-24">
                  <h2 className="text-3xl text-zinc-700 text-left mt-8 font-medium">
                    What Types of Reports Can v0 Report the AI Tool Generate?
                  </h2>
                  <div className="mx-auto rounded-xl max-w-screen-lg p-1">
                    <div className="grid grid-cols-1 gap-8 mx-auto mb-24">
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Survey Report</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            This type of report provides a detailed analysis of data collected from
                            surveys, such as customer satisfaction, employee engagement, or market
                            trends. It turns raw data into useful information that helps
                            organizations make informed decisions.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Performance Review</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            A performance review report offers a detailed evaluation of an
                            individual’s achievements, skills, and contributions over a certain
                            period. It's important for professional growth and aligning personal
                            achievements with the organisation's goals.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">
                            Marketing Report Templates Overview
                          </h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            This report gives a brief but thorough view of a company’s marketing
                            activities over time. It covers key marketing efforts, campaign results,
                            and performance metrics, providing insights that help refine strategies,
                            adjust budgets, and enhance future marketing efforts.
                          </p>
                        </div>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 w-fit">
                          <Check className="w-9 h-9 bg-yellow-100 text-yellow-700 p-2 rounded-full mr-2" />
                          <h3 className="text-lg font-medium ">Healthcare Report Maker Analysis</h3>
                        </div>
                        <div className="flex items-center gap-2 w-fit">
                          <div className="w-9 h-9 p-4 rounded-full mr-2" />
                          <p className="mt-4 text-zinc-500">
                            A healthcare analysis report examines important factors in the
                            healthcare sector, providing a deep dive into trends, performance
                            measures, and key indicators. It analyzes data on patient outcomes,
                            resource use, financial efficiency, and compliance with regulations.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="hidden sm:block">
                <ToolLinks />
              </div>
            </div>
            <div className="w-fit grid gap-8 bg-sky-50 rounded-xl py-8 md:py-20 px-5 md:px-24 mx-2 md:mx-auto">
              <p className="text-2xl font-semibold text-gray-900 ">Get Started Today!</p>
              <p className="text-xl tracking-tight font-medium text-zinc-600 grid gap-2">
                <span>
                  Embrace the efficiency of the automated AI report writing tool with the v0 AI
                  Report Generator.
                </span>
                <span>
                  Sign up now or request a demo to see how our report writer tool can revolutionize
                  how you work with your report data.{' '}
                </span>
                <span>Transform your report analysis with v0 Report's accuracy and speed!</span>
              </p>
            </div>
            <div className="w-11/12 mx-auto my-24 md:w-fit text-center grid gap-8">
              <Link
                rel="canonical"
                href={isSubscribed ? '/home' : '/'}
                className="flex justify-center w-fit mx-auto"
              >
                <Image
                  className="object-scale-down h-8 w-8"
                  src="/images/logo.png"
                  alt="Logo"
                  width={200}
                  height={300}
                />
                <p className="my-auto ml-4 hidden sm:block bg-gray-100 text-gray-600 px-3 py-1.5 rounded ">
                  v0 report
                </p>
              </Link>
              <h2 className="text-3xl tracking-tight font-semibold leading-tight text-gray-900 ">
                Experience the future of document generation today <br />
                with v0 Report!
              </h2>
              <Link
                className="w-fit mx-auto h-8 rounded-md px-4 inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 bg-sky-600 text-primary-foreground hover:bg-sky-600/90 "
                href={'/auth/signup'}
              >
                Get started
              </Link>
            </div>
          </div>
        </main>
      </NonAppNavWrapper>
    </>
  )
}
